import React, { useEffect, useState } from "react";

const SESSION_KEY = "gfn_splash_shown";
const DURATION_MS = 3600; // 3.6s

export default function SplashScreen({ onDone }) {
  const [progress, setProgress] = useState(0);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const pct = Math.min(1, (t - start) / DURATION_MS);
      setProgress(pct);
      if (pct < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setLeaving(true);
        // Match fade-out CSS transition (600ms)
        setTimeout(() => {
          try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) {}
          onDone && onDone();
        }, 600);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [onDone]);

  return (
    <div
      data-testid="splash-screen"
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-black transition-opacity duration-[600ms] ease-out ${leaving ? "opacity-0 pointer-events-none" : "opacity-100"}`}
    >
      {/* Radial red glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(180, 25, 40, 0.28) 0%, rgba(0,0,0,0) 60%)",
        }}
      />
      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      <div className="relative flex flex-col items-center px-6">
        {/* LIVE indicator */}
        <div
          className="flex items-center gap-2 mb-6 px-3 py-1 rounded-full border border-red-500/40 bg-red-500/10"
          data-testid="splash-live-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 live-ping" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500 live-dot" />
          </span>
          <span className="text-red-400 text-xs font-condensed tracking-[0.25em] uppercase">
            Live On Air
          </span>
        </div>

        {/* Brand */}
        <h1
          data-testid="splash-brand"
          className="font-display text-5xl sm:text-6xl md:text-7xl leading-none text-center tracking-wider text-gold-gradient"
        >
          GFN SOUL NYC
        </h1>

        <p className="mt-3 text-xs sm:text-sm font-condensed uppercase tracking-[0.35em] text-neutral-400">
          Radio · TV · Culture
        </p>

        {/* Big equalizer bars */}
        <div
          data-testid="splash-eq"
          className="mt-10 flex items-end gap-1.5 h-16"
          aria-hidden
        >
          {Array.from({ length: 9 }).map((_, i) => (
            <span
              key={i}
              className="splash-eq-bar"
              style={{
                animationDelay: `${(i % 5) * 90}ms`,
                animationDuration: `${700 + (i % 4) * 150}ms`,
              }}
            />
          ))}
        </div>

        {/* Progress bar */}
        <div className="mt-10 w-64 sm:w-80">
          <div className="h-[3px] w-full rounded-full bg-white/10 overflow-hidden">
            <div
              data-testid="splash-progress"
              className="h-full rounded-full"
              style={{
                width: `${Math.round(progress * 100)}%`,
                background:
                  "linear-gradient(90deg, #997A15 0%, #EAD385 45%, #D4AF37 55%, #997A15 100%)",
                transition: "width 80ms linear",
                boxShadow: "0 0 12px rgba(212, 175, 55, 0.55)",
              }}
            />
          </div>
          <div className="mt-2 flex justify-between text-[10px] font-condensed uppercase tracking-[0.3em] text-neutral-500">
            <span>Tuning In</span>
            <span>{Math.round(progress * 100)}%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function shouldShowSplash() {
  try {
    return !sessionStorage.getItem(SESSION_KEY);
  } catch (e) {
    return true;
  }
}
